import { Analytics } from '@analytics';
import {
  getCompanyCustomisation,
  updateCompanyBranding,
  updateTourPhotoShareCopy
} from '@api/company';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import AiReadinessSection from '@components/TapToTip/Setup/AiReadinessSection';
import { EXPERIMENTAL_AI_READINESS_BENCHMARK } from '@utils/experimentalFeatures';
import { canSeeOperatorSetup } from '@utils/helpers';
import BrandingSection from '@components/TapToTip/Setup/BrandingSection';
import ChoiceAdvisor from '@components/TapToTip/Setup/ChoiceAdvisor';
import CommissionSettings from '@components/TapToTip/Setup/CommissionSettings';
import CustomLinkSection from '@components/TapToTip/Setup/CustomLinkSection';
import LandingMessageSection from '@components/TapToTip/Setup/LandingMessageSection';
import LivePreviewPanel from '@components/TapToTip/Setup/LivePreviewPanel';
import { SetupPreviewProvider, useSetupPreview } from '@components/TapToTip/Setup/SetupPreviewContext';
import SocialSection from '@components/TapToTip/Setup/SocialSection';
import TourPhotoShareSection from '@components/TapToTip/Setup/TourPhotoShareSection';
import ToursSection from '@components/TapToTip/Setup/ToursSection';
import { isTourActive } from '@components/TapToTip/Setup/ToursSection/constants';
import UpsellSection from '@components/TapToTip/Setup/UpsellSection';
import { setupSubScreens, StyleBreakpoints } from '@constants';
import { makeStyles } from '@material-ui/core/styles';
import { formatRegionalLinks } from '@utils/helpers';
import { useIsFeatureForCompanyEnabled } from '@utils/hooks';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { useInjectReducer } from '../../../utils/injectReducer';
import { useInjectSaga } from '../../../utils/injectSaga';
import * as actions from './actions';
import { summarisePostTourChoices } from './choiceLoad';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import { pickActiveSection } from './scrollSpy';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

const segmentToSectionMap = new Map([
  ...(EXPERIMENTAL_AI_READINESS_BENCHMARK
    ? [['ai-readiness', 'AiReadiness']]
    : []),
  ['tours', 'Tours'],
  ['upsell', 'Upsell'],
  ['social', 'Social'],
  ['branding', 'Branding'],
  ['custom-link', 'CustomLink'],
  ['commission', 'Commission']
]);

// In visual (top-to-bottom) order — the scroll-spy picks the last section whose
// top has crossed the line, so order must match the render order below. The
// experimental sections are filtered out rather than left in: the spy measures
// each id's node, and an id that never renders would silently anchor at 0.
const SECTION_IDS = [
  ...(EXPERIMENTAL_AI_READINESS_BENCHMARK ? ['AiReadiness'] : []),
  'Tours',
  'LandingMessage',
  'Upsell',
  'Social',
  'Branding',
  'CustomLink'
];

// Tours arrive from the saga in their raw translated shape; the title lives
// under the primary language, the same way ToursSection reads it for its table.
const tourTitle = tour => {
  const lang = tour?.translations?.primaryLanguage;
  return tour?.translations?.translations?.[lang]?.data?.title || '';
};

const SetupInner = ({
  user,
  updateCompanyFeatures,
  updateCompanyExternalLink,
  deleteCompanyExternalLink,
  postTipPageFeatures,
  tours,
  fetchTours,
  addTapToTipTour,
  editTapToTipTour,
  agentsProfiles,
  fetchAgentsProfiles,
  multipleAssign,
  deleteTour,
  isLoading,
  updateCommissionSettings,
  paymentCommission
}) => {
  // Card 304 — Setup is operator-only, and says so itself rather than trusting
  // the route table. Every /tipdirect/setup route is adminOnly, but the five
  // sub-routes were not for a long time, which let a guide open the whole
  // operator Setup page — commission settings included — by typing the URL.
  // Guarding here too means a sub-route added without adminOnly fails closed.
  const isOperator = canSeeOperatorSetup(user);

  const { draft, updatePreview, setActiveSection } = useSetupPreview();
  const linkDraftsRef = useRef({});
  useInjectReducer({ key: 'setup', reducer });
  useInjectSaga({ key: 'setup', saga });
  const location = useLocation();
  const lastSegment = location.pathname.split('/').pop();
  const intl = useIntl();
  const classes = useStyles();
  const [activeSubScreen, setActiveSubScreen] = useState('');
  const isFeatureForCompanyEnabled = useIsFeatureForCompanyEnabled();
  const [externalLinks, setExternalLinks] = useState([]);
  // Brand config (card 61) lives on customizationDetails, not on features, so it
  // is loaded/saved directly rather than through the setup redux flow.
  const [branding, setBranding] = useState(null);
  // Stable client-side keys for link rows. New/unsaved rows have no server `_id`,
  // so we tag them with a unique `_key` to avoid React key collisions (which
  // otherwise make edits land in the wrong row / appear not to save).
  const newLinkCounter = useRef(0);
  const createBlankLink = () => ({ _key: `new-${(newLinkCounter.current += 1)}` });

  // take updated postTipPageFeatures or use one from the user object
  // Card 289 — the operator's share wording lives on features.tourPhotos,
  // beside the enabled flag Andy sets by hand. Held locally so a save reflects
  // immediately without a full company refetch.
  const [tourPhotos, setTourPhotos] = useState(
    user?.companyDetails?.features?.tourPhotos
  );

  const relevantPostTipPageFeatures = useMemo(
    () => postTipPageFeatures || user?.companyDetails?.features?.postTipPage,
    [user, postTipPageFeatures]
  );
  const relevantPaymentCommission = useMemo(
    () => paymentCommission || user?.companyDetails?.paymentCommission,
    [(user, paymentCommission)]
  );
  const linkUrl = formatRegionalLinks(`t/g/${user.userId}`);
  const previewTippingLink = `${linkUrl}?preview=true`;

  // Count the post-tour choices off the same draft the live preview renders, so
  // the advisor moves with an unsaved edit rather than lagging a save behind.
  // The draft only holds what a section has streamed (the Reviews toggle, for
  // one, has no control on this page), so saved features are the base and each
  // streamed key replaces its own.
  const choiceSummary = useMemo(
    () =>
      summarisePostTourChoices({
        features: { ...relevantPostTipPageFeatures, ...(draft?.features?.postTipPage || {}) },
        tours,
        tourTitle
      }),
    [relevantPostTipPageFeatures, draft, tours]
  );

  useEffect(() => {
    if (!relevantPostTipPageFeatures.externalLinks?.length) {
      setExternalLinks([createBlankLink()]);
    } else {
      setExternalLinks(
        relevantPostTipPageFeatures.externalLinks.map(link => ({
          ...link,
          _key: link._id
        }))
      );
    }
  }, [relevantPostTipPageFeatures]);

  useEffect(() => {
    if (!tours?.length) {
      fetchTours();
    }
    if (!agentsProfiles?.length) {
      fetchAgentsProfiles();
    }
  }, []);

  useEffect(() => {
    getCompanyCustomisation()
      .then(setBranding)
      .catch(() => setBranding({}));
  }, []);

  // Card 289 — dedicated endpoint, not the generic features one: that route
  // replaces the whole tourPhotos object and would wipe `enabled`.
  const handleSaveTourPhotoShare = values => {
    updateTourPhotoShareCopy(user.companyId, values)
      .then(company =>
        setTourPhotos(company?.companyDetails?.features?.tourPhotos || values)
      )
      .catch(err => console.error('Failed to save shared photo message', err));
  };

  const handleSaveBranding = values => {
    updateCompanyBranding(user.companyId, values)
      .then(() => setBranding(values))
      .catch(err => console.error('Failed to save branding', err));
  };

  // Live preview: stream the initial branding + tours so the panel reflects
  // saved state before the operator edits anything.
  useEffect(() => {
    if (branding) updatePreview({ branding });
  }, [branding, updatePreview]);

  // Only stream tours once loaded: arrays replace on merge, so streaming a
  // placeholder [] before the fetch resolves (or after it fails) would wipe the
  // real tours from the previewed rating screen. Stream only Active tours
  // (review link present) — the backend serves real customers only those, so
  // an unfiltered stream would preview Incomplete tours nobody can ever see.
  useEffect(() => {
    if (tours) updatePreview({ tours: tours.filter(isTourActive) });
  }, [tours, updatePreview]);

  // Assemble the full custom-link array (mixing live edits with saved rows) and
  // stream it whenever any link row changes.
  const streamLinks = () => {
    const assembled = externalLinks.map((link, i) => linkDraftsRef.current[i] || link);
    updatePreview({ features: { postTipPage: { externalLinks: assembled } } });
  };
  const handleLinkPreview = (index, values) => {
    linkDraftsRef.current[index] = { ...linkDraftsRef.current[index], ...values };
    streamLinks();
  };
  // The enable/disable toggle isn't a Formik field, so stream it explicitly.
  const handleLinkToggle = (index, enabled) => {
    linkDraftsRef.current[index] = {
      ...(linkDraftsRef.current[index] || externalLinks[index]),
      enabled
    };
    streamLinks();
  };

  // Scroll-spy: tell the preview which section the operator is looking at so it
  // can navigate to the matching screen. Uses a scroll line rather than an
  // IntersectionObserver band: the LAST section can't be scrolled into a narrow
  // band (the page ends first), so an observer never activates it and the preview
  // never follows to it. Instead pick the last section whose top has crossed a
  // line near the top, and force the last section once scrolled to the bottom.
  // See scrollSpy.js — the bottom snap only applies when the page can scroll, so
  // a mostly-empty (short) setup doesn't get pinned to the last screen on load.
  useEffect(() => {
    const LINE_RATIO = 0.35;
    let ticking = false;
    const compute = () => {
      ticking = false;
      const scroller = document.scrollingElement || document.documentElement;
      const sections = SECTION_IDS.map(id => {
        const el = document.getElementById(id);
        return el ? { id, top: el.getBoundingClientRect().top } : null;
      }).filter(Boolean);
      const active = pickActiveSection({
        sections,
        line: window.innerHeight * LINE_RATIO,
        scrollTop: window.scrollY,
        viewportHeight: window.innerHeight,
        scrollHeight: scroller.scrollHeight
      });
      if (active) setActiveSection(active);
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(compute);
      }
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [setActiveSection, branding, externalLinks.length]);

  const handleUpdateCompanyFeatures = feature => {
    updateCompanyFeatures(user.companyId, {
      postTipPage: {
        ...relevantPostTipPageFeatures,
        ...feature
      }
    });
  };

  const addExternalLink = () => {
    if (externalLinks.length >= 4) return;
    setExternalLinks([...externalLinks, createBlankLink()]);
  };

  const removeExternalLink = (link, index) => {
    // Saved links (with a server `_id`) are deleted on the backend; the saga
    // refetch reconciles the list. Unsaved rows are just dropped locally.
    if (link._id) {
      deleteCompanyExternalLink(user.companyId, link._id);
    }
    setExternalLinks(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateCommissionSettings = commissionSettings => {
    updateCommissionSettings(user.companyId, commissionSettings);
  };

  const handleReturn = () => {
    setActiveSubScreen('');
  };

  // Scroll-spy only follows scrolling, so an operator can start editing a box
  // that isn't the section currently mirrored in the preview (their scroll height
  // never lined up). Interacting with any field is a clear intent signal: point
  // the preview at that box's section immediately, regardless of scroll position.
  const sectionSelector = SECTION_IDS.map(id => `#${id}`).join(',');
  const handleSectionFocus = e => {
    const section = e.target.closest(sectionSelector);
    if (section?.id) setActiveSection(section.id);
  };

  const activeSubScreenComponent = {
    [setupSubScreens.commission]: (
      <CommissionSettings
        paymentCommission={relevantPaymentCommission}
        handleReturn={handleReturn}
        handleUpdateCommissionSettings={handleUpdateCommissionSettings}
      />
    )
    // add other sub screens here
  };

  // Card 304 — placed after every hook so hook order never changes between
  // renders, and before any operator data reaches the DOM.
  if (!isOperator) {
    return <Redirect to="/" />;
  }

  return (
    <>
      <TopNav />

      <div className={classes.container}>
        <TipDirectLeftMenu
          selectedTab={'Setup'}
          exactTab={segmentToSectionMap.get(lastSegment)}
          hideSubMenu={!!activeSubScreen}
        />

        <div className="flex flex-row justify-center xl:justify-start gap-8 w-full min-w-0 p-4 md:p-6">
          <div
            onFocusCapture={handleSectionFocus}
            className={`flex flex-col gap-6 w-full max-w-[540px] lg:max-w-[690px] xl:max-w-[750px] min-w-0 ${
              activeSubScreen ? '' : 'md:mx-0'
            }`}>
          {!activeSubScreen && (
            <>
              <div className="flex items-center justify-between gap-3 rounded-2xl border border-solid border-indigo-200 bg-indigo-50 py-1.5 pl-6 pr-1.5 text-xs sm:text-sm font-semibold text-indigo-900">
                <span>👀 {intl.formatMessage(messages.previewTDProfile)}</span>
                <a
                  className="inline-flex items-center justify-center bg-white hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2 rounded-xl border border-solid border-slate-300 transition-colors cursor-pointer no-underline whitespace-nowrap text-xs sm:text-sm"
                  onClick={() => {
                    Analytics.track('Preview TipDirect Profile clicked', {
                      page: 'setup'
                    });
                  }}
                  target="_blank"
                  href={previewTippingLink}>
                  {intl.formatMessage(messages.previewProfile)}
                </a>
              </div>

              {/* Above the sections it summarises, so an operator who only
                  comes in to flip one toggle still reads it. It is a summary of
                  the whole page rather than of anything near it, so it does not
                  carry a section id and the scroll-spy ignores it. */}
              <ChoiceAdvisor summary={choiceSummary} />

              {EXPERIMENTAL_AI_READINESS_BENCHMARK && (
                <AiReadinessSection companyId={user.companyId} />
              )}

              <ToursSection
                tours={tours}
                addTapToTipTour={addTapToTipTour}
                editTapToTipTour={editTapToTipTour}
                agentsProfiles={agentsProfiles}
                multipleAssign={multipleAssign}
                deleteTour={deleteTour}
                isLoading={isLoading}
              />

              <LandingMessageSection
                chooserTitle={relevantPostTipPageFeatures.chooserTitle}
                chooserText={relevantPostTipPageFeatures.chooserText}
                updateCompanyFeatures={handleUpdateCompanyFeatures}
              />
              <UpsellSection
                upsell={relevantPostTipPageFeatures.upsell}
                isEnabled={relevantPostTipPageFeatures.CustomerUpsell}
                updateCompanyFeatures={handleUpdateCompanyFeatures}
              />
              <SocialSection
                socialLinks={relevantPostTipPageFeatures.socialLinks}
                isEnabled={relevantPostTipPageFeatures.CustomerSocial}
                updateCompanyFeatures={handleUpdateCompanyFeatures}
              />
              {/* Card 289 — only for companies who actually have tour photos.
                  Strict === true, the same gate the rest of the feature uses:
                  there is nothing to configure until it is switched on. */}
              {tourPhotos?.enabled === true && (
                <TourPhotoShareSection
                  shareTitle={tourPhotos.shareTitle}
                  shareText={tourPhotos.shareText}
                  companyName={user?.companyName}
                  onSave={handleSaveTourPhotoShare}
                />
              )}
              {branding && (
                <BrandingSection
                  companyId={user.companyId}
                  branding={branding}
                  onSave={handleSaveBranding}
                />
              )}
              {externalLinks.map((link, index) => (
                <CustomLinkSection
                  key={link._id || link._key}
                  companyId={user.companyId}
                  externalLink={link}
                  updateCompanyExternalLink={updateCompanyExternalLink}
                  onRemove={() => removeExternalLink(link, index)}
                  onValuesChange={values => handleLinkPreview(index, values)}
                  onToggle={enabled => handleLinkToggle(index, enabled)}
                />
              ))}
              <div
                className="bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-6 flex flex-col justify-center items-center cursor-pointer"
                onClick={addExternalLink}
                role="button">
                <button
                  type="button"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-colors cursor-pointer border-0 text-sm">
                  <div>Add Custom Link +</div>
                </button>
                {externalLinks.length >= 4 && (
                  <div className="text-slate-500 text-sm font-normal mt-2">
                    Maximum of 4 custom links
                  </div>
                )}
              </div>
            </>
          )}
          {activeSubScreen && activeSubScreenComponent[activeSubScreen]}
          </div>

          {!activeSubScreen && <LivePreviewPanel userId={user.userId} locale={intl.locale} />}
        </div>
      </div>
    </>
  );
};

const Setup = props => (
  <SetupPreviewProvider>
    <SetupInner {...props} />
  </SetupPreviewProvider>
);

const mapStateToProps = state => ({
  user: state.user.user,
  postTipPageFeatures: state.setup?.postTipPageFeatures,
  isLoading: state.setup?.isLoading,
  tours: state.setup?.tours,
  agentsProfiles: state.setup?.agentsProfiles,
  paymentCommission: state.setup?.paymentCommission
});

const mapDispatchToProps = dispatch => ({
  updateCompanyFeatures: (companyId, features) =>
    dispatch(actions.updatePostTipFeature({ companyId, features })),
  fetchAgentsProfiles: () => dispatch(actions.fetchAgentsProfiles()),
  addTapToTipTour: payload => dispatch(actions.addTapToTipTour(payload)),
  editTapToTipTour: payload => dispatch(actions.editTapToTipTour(payload)),
  fetchTours: () => dispatch(actions.fetchTours()),
  multipleAssign: (tourIds, guideIds) => dispatch(actions.multipleAssign({ tourIds, guideIds })),
  deleteTour: payload => dispatch(actions.deleteTour(payload)),
  updateCompanyExternalLink: (companyId, externalLink) =>
    dispatch(actions.updateExternalLink({ companyId, externalLink })),
  deleteCompanyExternalLink: (companyId, linkId) =>
    dispatch(actions.deleteExternalLink({ companyId, linkId })),
  updateCommissionSettings: (companyId, commissionSettings) =>
    dispatch(actions.updateCommissionSettings({ companyId, commissionSettings }))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Setup);
